// apps/web/src/components/vendas/cliente-combobox.tsx — Fase 12, Plano 02 (PV-01, D-37).
// Seletor de cliente com busca (Popover + lista, padrão de tipo-material-combobox.tsx).
// Consulta GET /api/v1/vendas/lookups/clientes (pv:ler; devolve até 20 clientes ativos).
// Fase 13, Plano 14: a prop `endpoint` permite outra fonte (Saldos usa /api/v1/estoque/lookups/clientes, estoque:consultar,
// sem depender de pv:ler); o padrão continua sendo o de Vendas e os usos existentes não mudam.
import { useEffect, useRef, useState } from 'react';
import { Check, ChevronsUpDown, Loader2, Search } from 'lucide-react';
import type { ClienteLookupPv } from '@pieltec/contracts';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';

interface ClienteComboboxProps {
  readonly value: ClienteLookupPv | null;
  readonly onChange: (cliente: ClienteLookupPv) => void;
  readonly disabled?: boolean;
  readonly invalido?: boolean;
  readonly id?: string;
  /** Rota do lookup de clientes (mesmo formato: `?busca=` e lista de `{ id, codigo, nome }`). */
  readonly endpoint?: string;
}

const ATRASO_BUSCA_MS = 250;
const ENDPOINT_PADRAO = '/api/v1/vendas/lookups/clientes';

export function ClienteCombobox({ value, onChange, disabled = false, invalido = false, id, endpoint = ENDPOINT_PADRAO }: ClienteComboboxProps) {
  const [open, setOpen] = useState(false);
  const [busca, setBusca] = useState('');
  const [clientes, setClientes] = useState<readonly ClienteLookupPv[]>([]);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const buscaRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    let ativo = true;
    setCarregando(true);
    setErro(null);
    const timer = window.setTimeout(() => {
      const termo = busca.trim();
      const query = termo ? `?busca=${encodeURIComponent(termo)}` : '';
      void fetch(`${endpoint}${query}`, { credentials: 'include' })
        .then(async (res) => {
          if (!ativo) return;
          if (!res.ok) {
            setErro('Não foi possível carregar os clientes.');
            setClientes([]);
            return;
          }
          setClientes((await res.json()) as ClienteLookupPv[]);
        })
        .catch(() => {
          if (ativo) setErro('Não foi possível carregar os clientes.');
        })
        .finally(() => {
          if (ativo) setCarregando(false);
        });
    }, ATRASO_BUSCA_MS);
    return () => {
      ativo = false;
      window.clearTimeout(timer);
    };
  }, [open, busca, endpoint]);

  return (
    <Popover
      open={open}
      onOpenChange={(proximo) => {
        setOpen(proximo);
        if (!proximo) setBusca('');
      }}
    >
      <PopoverTrigger asChild>
        <button
          id={id}
          type="button"
          disabled={disabled}
          aria-expanded={open}
          aria-haspopup="listbox"
          aria-invalid={invalido || undefined}
          className={`ui-control w-full flex items-center justify-between min-h-[var(--control-height,2rem)] px-3 py-2 bg-white border rounded-md text-sm text-slate-800 disabled:opacity-50 ${
            invalido ? 'border-rose-500' : 'border-slate-300'
          }`}
        >
          <span className="truncate">{value ? `${value.nome} (${value.codigo})` : 'Selecione o cliente...'}</span>
          <ChevronsUpDown className="w-4 h-4 text-slate-400 shrink-0 ml-2" aria-hidden="true" />
        </button>
      </PopoverTrigger>

      <PopoverContent
        className="p-0 overflow-hidden"
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          buscaRef.current?.focus();
        }}
      >
        <div className="p-2 border-b bg-slate-50 flex items-center gap-2">
          <Search className="w-3.5 h-3.5 text-slate-400 shrink-0" aria-hidden="true" />
          <input
            ref={buscaRef}
            type="text"
            aria-label="Buscar cliente"
            placeholder="Buscar por nome ou código do cliente"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            className="w-full bg-transparent text-sm text-slate-900 focus:outline-none"
          />
        </div>
        <div className="max-h-60 overflow-y-auto p-1" role="listbox" aria-label="Clientes">
          {carregando ? (
            <div className="p-4 text-center text-sm text-slate-500 flex items-center justify-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
              Carregando clientes...
            </div>
          ) : erro ? (
            <div role="alert" className="p-4 text-center text-sm text-rose-700">
              {erro}
            </div>
          ) : clientes.length === 0 ? (
            <div className="p-4 text-center text-sm text-slate-500">Nenhum cliente encontrado.</div>
          ) : (
            clientes.map((cliente) => {
              const selecionado = value?.id === cliente.id;
              return (
                <button
                  key={cliente.id}
                  type="button"
                  role="option"
                  aria-selected={selecionado}
                  onClick={() => {
                    onChange(cliente);
                    setOpen(false);
                    setBusca('');
                  }}
                  className={`w-full min-h-[var(--control-height,2rem)] flex items-center justify-between px-3 py-2 text-left text-sm rounded-md transition-colors ${
                    selecionado ? 'bg-slate-900 text-white font-semibold' : 'text-slate-700 hover:bg-slate-100'
                  }`}
                >
                  <span className="flex flex-col truncate pr-2">
                    <span className="truncate">{cliente.nome}</span>
                    <span className={`text-xs ${selecionado ? 'text-slate-300' : 'text-slate-500'}`}>{cliente.codigo}</span>
                  </span>
                  {selecionado && <Check className="w-4 h-4 shrink-0" aria-hidden="true" />}
                </button>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
